import { flatten, uniq } from 'lodash';
import type { RefObject } from 'react';
import { forwardRef, memo, useCallback, useId } from 'react';
import type { FileError } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { MIME_TYPES } from '../../constants';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import InlineTextButton from '../inline-text-button/inline-text-button';
import InputFeedback from '../input-feedback';
import Spinner from '../spinner/spinner';
import Typography from '../typography/typography';

const MEGABYTES_MULTIPLIER = 1_000_000;

export enum UploadDocumentErrorCode {
  FileInvalidType = 'file-invalid-type',
  FileTooLarge = 'file-too-large',
  FilenameTooLong = 'filename-too-long',
  TooManyFiles = 'too-many-files',
  UploadError = 'upload-error',
}

const MAX_FILENAME_LENGTH = 155;

interface IDropzoneContainer {
  $isDragActive: boolean;
  $uploading: boolean;
  $uploadError: boolean;
  disabled: boolean;
  theme: ITheme;
  height?: string;
}

export interface IFileUploader {
  onFileUpload: (files: File[]) => void;
  uploading: boolean;
  acceptedFileExtensions: string[];
  maxFilesNumber?: number;
  maxFileSize?: number;
  maxFilenameLength?: number;
  uploadError?: string;
  disabled?: boolean;
  height?: string;
  name?: string;
  uploaderTitleId?: string;
  uploadLinkPrefix?: string;
  uploadLinkLabel?: string;
}

export const FileUploader = forwardRef(
  (
    {
      onFileUpload,
      uploading,
      acceptedFileExtensions,
      disabled = false,
      maxFilesNumber = 0, //no limit on number of files by default
      maxFileSize = 4, // 4MB by default
      maxFilenameLength = MAX_FILENAME_LENGTH,
      uploadError,
      height,
      name = 'document-upload',
      uploaderTitleId,
      uploadLinkPrefix,
      uploadLinkLabel,
    }: IFileUploader,
    ref
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const documentUploadErrorId = useId();
    const fileRequirementsId = useId();

    const validateFilenameLength = useCallback(
      (file: File): FileError | null => {
        if (!isFilenameTooLong(file, maxFilenameLength)) {
          return null;
        }
        return {
          code: UploadDocumentErrorCode.FilenameTooLong,
          message: `Filename exceeds ${maxFilenameLength} characters`,
        };
      },
      [maxFilenameLength]
    );

    const { getRootProps, getInputProps, open, isDragActive, fileRejections } =
      useDropzone({
        noClick: true,
        noKeyboard: true,
        maxSize: maxFileSize * MEGABYTES_MULTIPLIER,
        accept: groupByMimeType(acceptedFileExtensions),
        maxFiles: maxFilesNumber,
        multiple: maxFilesNumber !== 1,
        validator: validateFilenameLength,
        onDropAccepted: onFileUpload,
        disabled: disabled || uploading,
      });

    const errorCodes = uniq(
      flatten(
        fileRejections?.map((fileRejection) =>
          fileRejection?.errors?.map((error) => error.code)
        ) ?? []
      )
    );

    if (acceptedFileExtensions.length === 0) {
      return;
    }
    const lastAcceptedFileExtension = formatFileExtension(
      acceptedFileExtensions?.at(-1)
    );
    const acceptedFileExtensionsText =
      acceptedFileExtensions?.slice(0, -1).length > 0
        ? acceptedFileExtensions
            .slice(0, -1)
            .map((extension) => extension.toUpperCase())
            .join(', ') +
          ` ${t('documentUpload.orLabel', { defaultValue: 'or' })} ` +
          lastAcceptedFileExtension
        : lastAcceptedFileExtension;

    const errorCodesMap = {
      [UploadDocumentErrorCode.FileTooLarge]: `${t(
        'documentUpload.errors.fileTooLarge',
        { defaultValue: 'Upload error. Files can have a maximum file size of' }
      )} ${maxFileSize}MB.`,
      [UploadDocumentErrorCode.FileInvalidType]: `${t(
        'documentUpload.errors.fileInvalidType',
        {
          defaultValue: 'Upload error. Files must be in one of these formats:',
        }
      )} ${acceptedFileExtensionsText}.`,
      [UploadDocumentErrorCode.FilenameTooLong]: t(
        'documentUpload.errors.filenameTooLong',
        {
          defaultValue:
            'Upload error. Filename is too long. Maximum allowed length is {{maxLength}} characters.',
          maxLength: maxFilenameLength,
        }
      ),
      [UploadDocumentErrorCode.TooManyFiles]: `${t(
        'documentUpload.errors.tooManyFiles',
        { defaultValue: 'Upload error. You can only upload up to' }
      )} ${maxFilesNumber}.`,
      [UploadDocumentErrorCode.UploadError]: t(
        'documentUpload.errors.uploadError',
        { defaultValue: 'Upload error. Please try again or contact support.' }
      ),
    };

    return (
      <Container>
        <DropzoneContainer
          ref={ref as unknown as RefObject<HTMLDivElement | null>}
          $isDragActive={isDragActive}
          $uploading={uploading}
          $uploadError={!!uploadError}
          disabled={disabled}
          height={height}
          {...getRootProps({ className: 'dropzone' })}
        >
          <DropzoneContent
            data-testid={`${name}-dropzone`}
            $isDragActive={isDragActive}
            $uploading={uploading}
            disabled={disabled}
          >
            {uploading ? (
              <LoadingContainer>
                <Typography
                  style={{
                    textAlign: 'center',
                    marginBottom: theme.spacing(1),
                  }}
                  variant={TypographyVariant.bodyMd1}
                >
                  {t('documentUpload.uploading', {
                    defaultValue: 'Uploading',
                  })}
                </Typography>
                <Spinner data-testid="spinner" size="30px" />
              </LoadingContainer>
            ) : (
              <>
                <DragAndDropTypography
                  variant={TypographyVariant.bodyMd1}
                  color={
                    disabled
                      ? theme.palette.neutral[70]
                      : theme.palette.neutral[100]
                  }
                >
                  {uploadLinkPrefix ??
                    t('documentUpload.textBeforeOpenFileDialogButton', {
                      defaultValue: 'Drag and drop or click to',
                    })}
                  &nbsp;
                  <InlineTextButton
                    button-id={name}
                    aria-describedby={`${
                      uploaderTitleId ?? ''
                    } ${fileRequirementsId}${
                      uploadError || errorCodes.length > 0
                        ? ` ${documentUploadErrorId}`
                        : ''
                    }`}
                    data-testid="click-here-button"
                    onClick={open}
                    label={
                      uploadLinkLabel ??
                      t('documentUpload.openFileDialogButtonText', {
                        defaultValue: 'upload a file',
                      })
                    }
                    disabled={disabled}
                    isUnderlined
                    noAriaLabel
                  ></InlineTextButton>
                </DragAndDropTypography>
                <Typography
                  id={fileRequirementsId}
                  variant={TypographyVariant.bodyMd1}
                  color={
                    disabled
                      ? theme.palette.neutral[70]
                      : theme.palette.neutral[90]
                  }
                >
                  {acceptedFileExtensionsText} ({maxFileSize}{' '}
                  {t('documentUpload.maxFileSize', {
                    defaultValue: 'MB max file size',
                  })}
                  )
                </Typography>
                <input
                  {...getInputProps()}
                  aria-label={t('documentUpload.openFileDialogButtonText', {
                    defaultValue: 'upload a file',
                  })}
                />{' '}
              </>
            )}
          </DropzoneContent>
        </DropzoneContainer>
        {errorCodes?.map((errorCode) =>
          errorCodesMap.hasOwnProperty(errorCode) ? (
            <InputFeedback
              key={errorCode}
              errorMessage={
                errorCodesMap[errorCode as keyof typeof errorCodesMap]
              }
              id={documentUploadErrorId}
            />
          ) : undefined
        )}
        <InputFeedback errorMessage={uploadError} id={documentUploadErrorId} />
      </Container>
    );
  }
);

FileUploader.displayName = 'FileUploader';

export default memo(FileUploader);

const getBackgroundColor = (
  $uploading: boolean,
  $isDragActive: boolean,
  disabled: boolean,
  theme: ITheme
) => {
  if ($uploading || $isDragActive) return theme.palette.primary.bg;
  if (disabled) return theme.palette.neutral[50];
  return theme.palette.neutral['00'];
};

const formatFileExtension = (fileExtension: string | undefined) => {
  if (!fileExtension) {
    return '';
  }
  return fileExtension.toUpperCase();
};

/**
 * react-dropzone types its validator as taking a `File`, but while a file is
 * dragged it runs against `DataTransferItem`-derived objects, which only expose
 * `kind` and `type` — the filename is not available until the file is dropped.
 */
export const isFilenameTooLong = (
  file: File | DataTransferItem,
  maxFilenameLength: number
) => 'name' in file && file.name.length > maxFilenameLength;

export const groupByMimeType = (fileExtensions: string[]) => {
  const result: Record<string, string[]> = {};
  for (const extension of fileExtensions) {
    const formattedExtension = `.${extension.toLowerCase()}`;
    const mimeType = MIME_TYPES.hasOwnProperty(formattedExtension)
      ? MIME_TYPES[formattedExtension]
      : undefined;
    if (mimeType) {
      result[mimeType] = result[mimeType] || [];
      result[mimeType].push(formattedExtension);
    } else {
      console.warn(`MIME type not found for "${formattedExtension}"`);
    }
  }
  return result;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropzoneContainer = styled.div`
  ${({
    $isDragActive,
    $uploading,
    $uploadError,
    disabled,
    height,
    theme,
  }: IDropzoneContainer) => css`
    padding: ${theme.spacing(2)};
    min-height: ${height ?? '63px'};
    border: 0.5px dashed
      ${$uploadError ? theme.palette.error.main : theme.palette.neutral[90]};
    border-radius: 2px;
    background-color: ${getBackgroundColor(
      $uploading,
      $isDragActive,
      disabled,
      theme
    )};
  `}
`;
const DropzoneContent = styled.div`
  ${({
    $isDragActive,
    $uploading,
    disabled,
    theme,
  }: {
    $isDragActive: boolean;
    $uploading: boolean;
    disabled: boolean;
    theme: ITheme;
  }) => css`
    width: 100%;
    height: 100%;
    min-height: inherit;
    background-color: ${getBackgroundColor(
      $uploading,
      $isDragActive,
      disabled,
      theme
    )};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  `}
`;
const LoadingContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  align-items: center;
`;

const DragAndDropTypography = styled(Typography)`
  ${({ theme }) => css`
    display: flex;
    align-items: baseline;
    margin-bottom: ${theme.spacing(1)};
  `}
`;
